import { EyeOff, Lock } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { AI_ACCESS_SUSPENDED } from '@papan/types'

/**
 * The tree row's access marker — an AI access limit (`hidden` / `r`) or the person's
 * own read-only access. ONE component for the workspace explorer and the linked-folder
 * tree (they used to carry a copy each).
 *
 * While AI access is SUSPENDED (#976) a stored limit is shown faded with a "paused"
 * explanation — the setting is kept and comes back when the feature does, but it does
 * not apply now. The person's own read-only lock wins then: it is the one that applies.
 */
export function AIOverrideBadge({ override, userReadOnly, suspended = AI_ACCESS_SUSPENDED }: {
  override?: 'hidden' | 'r'
  userReadOnly?: boolean
  suspended?: boolean
}) {
  const { t } = useTranslation('files')
  if (suspended) {
    if (userReadOnly) return <span title="Read-only access"><Lock size={10} className="shrink-0 text-blue-300/60" /></span>
    if (override) {
      const Icon = override === 'hidden' ? EyeOff : Lock
      return (
        <span data-testid="ai-access-paused" title={t(override === 'hidden' ? 'aiAccess.pausedHidden' : 'aiAccess.pausedReadOnly')}>
          <Icon size={10} className="shrink-0 themed-text-muted opacity-40" />
        </span>
      )
    }
    return null
  }
  if (override === 'hidden') {
    return <span title="Files hidden from AI"><EyeOff size={10} className="shrink-0 themed-text-muted" /></span>
  }
  if (override === 'r') {
    return <span title="AI Read-only"><Lock size={10} className="shrink-0 themed-text-muted" /></span>
  }
  if (userReadOnly) {
    return <span title="Read-only access"><Lock size={10} className="shrink-0 text-blue-300/60" /></span>
  }
  return null
}
